/* eslint-disable camelcase, extra-rules/no-commented-out-code, max-lines, sonarjs/cognitive-complexity  */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import dynamic from 'next/dynamic';
import {
  configureInternalNavigationFromCMSUrl,
  getAPIConfig,
  getViewportInfo,
  isIEBrowser,
  isSafariBrowser,
  badgeTranformationPrefix,
  removeExtension,
  parseBoolean,
  isAppleDevice,
  getAspectRatio,
  updateUrlWithIcid,
} from '@tcp/core/src/utils';
import { getImgBackgroundStyleObjForBrand } from '@tcp/core/src/utils/utils.web';
import { isTier1Device } from '@tcp/web/src/utils/device-tiering';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import LabelsContext from '@tcp/core/src/components/common/context/LabelsContext';
import ModuleQuickViewWrapper from '@tcp/core/src/components/common/molecules/ModuleQuickViewWrapper';
import { getPictureBlockV2 } from '../molecules/DamImageV2';
import ImageSource from '../molecules/ImageSource.view';
import ImageTag from '../molecules/ImageTag.view';
import RenderVideo from '../molecules/RenderVideo.view';
import PlpPopUpMedia from '../molecules/PlpPopUpMedia';
import {
  cleanUpUrl,
  getImageSource,
  getImageSourcesV2,
  getVideoUrls,
  getVideoPosterPreload,
  getPosterUrls,
  findPosterImage,
  getAssetHostVal,
  isAbsoluteUrl,
} from '../utils/DamImageUtils';
import {
  StyledImage,
  StyledFigureTag,
  StyledFigCaption,
  getImageStyle,
  getVideoStyle,
} from '../DamImage.styles';
import PinchZoomImage from '../../../../features/browse/ProductDetail/PinchZoomImage/views/PinchZoomImage';

const defaultImageCrop = 'w_auto,f_auto,q_auto';
export const isGif = (path) => /\.gif$/i.test(path);
export const isVideo = (path) => /\.(mp4|mov|webm)/i.test(path);
export const replaceGifWithWebp = (url) => (isGif(url) ? url.replace('.gif', '.webp') : url);

const CloudinaryVideoPlayer = dynamic(() => {
  return import(
    /* webpackChunkName: "CloudinaryVideoPlayer" */ '@tcp/core/src/components/common/atoms/CloudinaryVideoPlayer'
  );
});

export const addOptimizedCrops = (input = '', isSecureImageFlagEnabled, imgData) => {
  let additional;
  if (isSecureImageFlagEnabled) {
    let additionalImgData = imgData.url.replace('/', ':');
    additionalImgData = `${additionalImgData}/t_plp_img_d,f_auto`;
    additional = [additionalImgData];
  } else {
    additional = ['f_auto', 'q_auto'];
  }
  const incoming = input.length > 0 ? input.split(',') : [];
  additional.forEach((crop) => {
    if (incoming.indexOf(crop) === -1) {
      incoming.push(crop);
    }
  });
  if (isSecureImageFlagEnabled) {
    return incoming.length ? incoming.join(':') : '';
  }
  return incoming.length ? incoming.join(',') : '';
};

export const getCropParams = (imgData, imgConfigs = [], badgeParams, isSecureImageFlagEnabled) => {
  const { crop_m: cropM, crop_t: cropT, crop_d: cropD } = imgData;
  const mobile = cropM || imgConfigs[0];
  const tablet = cropT || imgConfigs[1];
  const desktop = cropD || imgConfigs[2];
  if (badgeParams != null) {
    const { desktop: badgeD, tablet: badgeT, mobile: badgeM } = badgeParams;
    return {
      mobile: `${badgeM}/${mobile}`,
      tablet: `${badgeT}/${tablet}`,
      desktop: `${badgeD}/${desktop}`,
    };
  }
  return {
    mobile: addOptimizedCrops(mobile, isSecureImageFlagEnabled, imgData),
    tablet: addOptimizedCrops(tablet, isSecureImageFlagEnabled, imgData),
    desktop: addOptimizedCrops(desktop, isSecureImageFlagEnabled, imgData),
  };
};

export const getImageUrls = (
  imgData = {},
  itemBrand = null,
  isProductImage = false,
  primaryBrand,
  isSecureImageFlagEnabled
) => {
  const { url_m, url_t, url, url_m_es, url_t_es, url_img_es, url_m_fr, url_t_fr, url_img_fr } =
    imgData;
  let mobile = url_m;
  let tablet = url_t;
  let desktop = url;
  const apiConfig = getAPIConfig();
  const { language, brandId } = apiConfig;
  let brand = brandId;
  if (itemBrand) {
    brand = itemBrand;
  }
  if (primaryBrand) {
    brand = primaryBrand;
  }
  if (language === 'es') {
    mobile = url_m_es || mobile;
    tablet = url_t_es || tablet;
    desktop = url_img_es || desktop;
  }
  if (language === 'fr') {
    mobile = url_m_fr || mobile;
    tablet = url_t_fr || tablet;
    desktop = url_img_fr || desktop;
  }
  const xs =
    replaceGifWithWebp(mobile) || replaceGifWithWebp(tablet) || replaceGifWithWebp(desktop);
  const sm = replaceGifWithWebp(tablet) || replaceGifWithWebp(desktop);
  const lg = replaceGifWithWebp(desktop);
  let assetPrefix;
  if (isProductImage) {
    if (isSecureImageFlagEnabled && brand === 'gym') {
      assetPrefix = 'v1/ecom/assets/products/gym_stylitics';
    } else {
      assetPrefix = apiConfig[`productAssetPath${brand.toUpperCase()}`];
    }
    return {
      mobile: `${assetPrefix}/${xs}`,
      tablet: `${assetPrefix}/${sm}`,
      desktop: `${assetPrefix}/${lg}`,
    };
  }
  return { mobile: xs, tablet: sm, desktop: lg };
};

export const getImageSourceString = (img, crop, render2x) => {
  const { assetHostTCP: assetHost } = getAPIConfig();
  const path = cleanUpUrl(img);
  const assetHostVal = getAssetHostVal(img, assetHost);
  return render2x
    ? [
        { density: '1x', url: `${assetHostVal}/${crop}/${path}` },
        { density: '2x', url: `${assetHostVal}/${crop},dpr_2/${path}` },
      ]
    : [
        { density: '1x', url: `${assetHostVal}/${crop}/${path}` },
        { density: '2x', url: `${assetHostVal}/${crop},dpr_1.5/${path}` },
        { density: '3x', url: `${assetHostVal}/${crop},dpr_3/${path}` },
      ];
};

export const getImageSources = (imgPaths, cropParams, render2x) => {
  const desktop = getImageSourceString(imgPaths.desktop, cropParams.desktop, render2x);
  const tablet = getImageSourceString(imgPaths.tablet, cropParams.tablet, render2x);
  const mobile = getImageSourceString(imgPaths.mobile, cropParams.mobile, render2x);
  return {
    desktop,
    tablet,
    mobile,
  };
};

export const getBadgedUrl = (dynamicBadgeOverlayQty, badgeConf, tcpStyleType) => {
  if (!dynamicBadgeOverlayQty || !badgeConf) return null;
  const badgeTranformation = badgeTranformationPrefix(tcpStyleType);
  const [desktop, tablet, mobile] = badgeConf;

  return {
    desktop: `${badgeTranformation}${dynamicBadgeOverlayQty},${desktop}`,
    tablet: `${badgeTranformation}${dynamicBadgeOverlayQty},${tablet}`,
    mobile: `${badgeTranformation}${dynamicBadgeOverlayQty},${mobile}`,
  };
};

export const breakFavouriteImageUrl = (img) => {
  if (img) {
    const url = img.split('/');
    return `${url[url.length - 2]}/${url[url.length - 1]}`;
  }
  return '';
};

export const getImageSourceForSyte = (props) => {
  const {
    imgData,
    isProductImage,
    itemBrand,
    fromFavorites,
    primaryBrand,
    imgConfigs,
    isSecureImageFlagEnabled,
    dynamicBadgeOverlayQty,
    badgeConfig,
    tcpStyleType,
  } = props;
  const { assetHostTCP: assetHost } = getAPIConfig();
  let { imageURL } = props;
  if (fromFavorites && !imageURL) {
    imageURL = imgData.url;
  }
  if (fromFavorites && !imgData.url) {
    imgData.url = breakFavouriteImageUrl(imageURL);
  }
  const imgPaths = getImageUrls(imgData, itemBrand, isProductImage, primaryBrand);
  const badgeParams = getBadgedUrl(dynamicBadgeOverlayQty, badgeConfig, tcpStyleType);
  const cropParams = getCropParams(imgData, imgConfigs, badgeParams, isSecureImageFlagEnabled);
  const imgSrc = { url: imgPaths.mobile, crop: cropParams.mobile, primaryBrand };
  return getImageSource(assetHost, imgSrc.crop, cleanUpUrl(imgSrc.url));
};

const imageConfigCart = ['t_t_cart_item_tile_m', 't_t_cart_item_tile_t', 't_t_cart_item_tile_d'];
// eslint-disable-next-line complexity
export const RenderImage = (props) => {
  const {
    imgData,
    imgConfigs,
    alt,
    isProductImage,
    dynamicBadgeOverlayQty,
    badgeConfig,
    tcpStyleType,
    className,
    itemBrand,
    isHomePage,
    isSecureImageFlagEnabled,
    fromFavorites,
    primaryBrand,
    tabIndex,
    onLoad,
    preloadImage,
    lazyLoadImage,
    isDamImageV2Enabled,
    isDamImageV2EnabledPDP,
    isDamImageV2EnabledPLP,
    isPinchZoomImage,
    selectedPDPImage,
    isPDP,
    fromQv,
    imageConfigWithoutDPR = '',
    fromBagPage = false,
    isFromHeroProductTile = false,
    isAddedToBagModal,
    isPngImage,
    isTransparentImageSizeReduceEnabled,
  } = props;
  const { extraConfig } = React.useContext(LabelsContext);
  const { addBgdToTransparentImgEnabled, backgroundColorsForPng } = extraConfig || {};
  const aspectRatioObj = getAspectRatio(imgData, null, null);
  let { imageURL } = props;
  const apiConfig = getAPIConfig();
  const { language } = apiConfig;

  if (fromFavorites && !imageURL) {
    imageURL = imgData.url;
  }
  if (fromFavorites && !imgData.url) {
    imgData.url = breakFavouriteImageUrl(imageURL);
  }

  const imgPaths = getImageUrls(imgData, itemBrand, isProductImage, primaryBrand);
  const badgeParams = getBadgedUrl(dynamicBadgeOverlayQty, badgeConfig, tcpStyleType);
  const cropParams = getCropParams(imgData, imgConfigs, badgeParams, isSecureImageFlagEnabled);
  const sources = getImageSources(imgPaths, cropParams, !isHomePage && !isProductImage);
  const imgSrc = { url: imgPaths.mobile, crop: cropParams.mobile, primaryBrand };
  let cropParamsCart = {};
  let imgSrcCart = {};
  if (fromBagPage) {
    cropParamsCart = getCropParams(imgData, imageConfigCart, badgeParams, isSecureImageFlagEnabled);
    imgSrcCart = { url: imgPaths.mobile, crop: cropParamsCart.mobile, primaryBrand };
  }

  if (addBgdToTransparentImgEnabled && isProductImage && backgroundColorsForPng) {
    imgData.backGroundStyle = getImgBackgroundStyleObjForBrand(
      addBgdToTransparentImgEnabled,
      backgroundColorsForPng,
      imgSrc.url
    );
  }

  let pictureBlock;

  if ((isDamImageV2Enabled && isHomePage) || isDamImageV2EnabledPLP || isDamImageV2EnabledPDP) {
    const imageSourcesV2 = getImageSourcesV2(imgPaths, cropParams, false, isFromHeroProductTile);
    const imageSourcesV2Cart = fromBagPage && getImageSourcesV2(imgPaths, cropParamsCart);
    let imageSourcesV2WithoutDPR = {};
    if (imageConfigWithoutDPR)
      imageSourcesV2WithoutDPR = getImageSourcesV2(imgPaths, cropParams, imageConfigWithoutDPR);

    pictureBlock = getPictureBlockV2({
      imageSourcesV2,
      preloadImage,
      imgSrc,
      alt,
      primaryBrand,
      imgData,
      className,
      tabIndex,
      onLoad,
      isProductImage,
      lazyLoadImage,
      isDamImageV2EnabledPLP,
      imageSourcesV2WithoutDPR,
      fromBagPage,
      imgSrcCart,
      imageSourcesV2Cart,
    });
  } else if (isPinchZoomImage) {
    pictureBlock = (
      <PinchZoomImage
        imgSrc={imgSrc}
        imgData={imgData}
        alt={alt}
        className={className}
        primaryBrand={primaryBrand}
        tabIndex={tabIndex}
        onLoad={onLoad}
        isProductImage={isProductImage}
        lazyLoadImage={lazyLoadImage}
        selectedPDPImage={selectedPDPImage}
        isPDP={isPDP}
        fromQv={fromQv}
      />
    );
  } else {
    pictureBlock = (
      <picture>
        <ImageSource
          srcSet={sources.desktop}
          media="(min-width: 1200px)"
          preloadImage={preloadImage}
        />
        <ImageSource
          srcSet={sources.tablet}
          media="(min-width: 768px) and (max-width:1199px)"
          preloadImage={preloadImage}
        />
        <ImageSource
          srcSet={sources.mobile}
          media="(max-width: 767px)"
          preloadImage={preloadImage}
        />
        <ImageTag
          src={imgSrc.url}
          crop={imgSrc.crop}
          altText={alt}
          overrideStyle={getImageStyle(imgData)}
          className={
            isPngImage && isTransparentImageSizeReduceEnabled
              ? `${className} png-image-resize`
              : className
          }
          primaryBrand={primaryBrand}
          tabIndex={tabIndex}
          onLoad={onLoad}
          backgroundStyle={
            isAddedToBagModal && imgData?.backGroundStyle?.backgroundImage
              ? undefined
              : imgData?.backGroundStyle
          }
          isProductImage={isProductImage}
          lazyLoadImage={lazyLoadImage}
        />
      </picture>
    );
  }

  const isTranslationTextEnabled = language !== 'en' && parseBoolean(imgData.useTranslationText);

  return isTranslationTextEnabled ? (
    <StyledFigureTag id="translation_container" aspectRatioObj={aspectRatioObj}>
      {pictureBlock}
      <StyledFigCaption id="translation_text">{alt}</StyledFigCaption>
    </StyledFigureTag>
  ) : (
    pictureBlock
  );
};

RenderImage.propTypes = {
  imgData: PropTypes.shape({}),
  imgConfigs: PropTypes.arrayOf(PropTypes.string),
  alt: PropTypes.string,
  isProductImage: PropTypes.bool,
  noSourcePictureTag: PropTypes.bool,
  className: PropTypes.string,
  itemBrand: PropTypes.string,
  preloadImage: PropTypes.bool,
  lazyLoadImage: PropTypes.bool,
  isTransparentImageSizeReduceEnabled: PropTypes.bool,
  isPngImage: PropTypes.bool,
};

RenderImage.defaultProps = {
  imgData: {},
  imgConfigs: [defaultImageCrop, defaultImageCrop, defaultImageCrop],
  alt: '',
  isProductImage: false,
  noSourcePictureTag: false,
  className: '',
  itemBrand: null,
  preloadImage: false,
  lazyLoadImage: false,
  isTransparentImageSizeReduceEnabled: false,
  isPngImage: false,
};

/* eslint-enable camelcase */

export const RenderVideoImage = (props) => {
  const { videoData, preloadImage, aspectRatioObj, className } = props;
  const videoUrls = getVideoUrls(videoData);
  const posterUrls = getPosterUrls(videoData);
  const mobilePoster = findPosterImage(posterUrls, videoUrls, 'mobile');

  const [poster, setPoster] = useState(mobilePoster);
  const desktopPoster = findPosterImage(posterUrls, videoUrls, 'desktop');
  const videoPosterPreload = getVideoPosterPreload(preloadImage, desktopPoster, mobilePoster);
  const defaultAspectRatioObj = getAspectRatio(null, videoData, null);
  useEffect(() => {
    const { isDesktop, isTablet, isMobile } = getViewportInfo();
    if (isDesktop && videoUrls.desktop) {
      setPoster(desktopPoster);
    }
    if (isTablet && videoUrls.tablet) {
      setPoster(findPosterImage(posterUrls, videoUrls, 'tablet'));
    }
    if (isMobile && videoUrls.mobile) {
      setPoster(findPosterImage(posterUrls, videoUrls, 'mobile'));
    }
  }, [videoData]);

  return (
    <>
      {videoPosterPreload}
      <StyledImage
        src={poster}
        alt={videoData.title}
        aspectRatioObj={{ ...defaultAspectRatioObj, ...aspectRatioObj }}
        className={className}
      />
    </>
  );
};

RenderVideoImage.propTypes = {
  videoData: PropTypes.shape({}),
  preloadImage: PropTypes.bool,
  aspectRatioObj: PropTypes.shape({}),
};

RenderVideoImage.defaultProps = {
  videoData: {},
  preloadImage: false,
  aspectRatioObj: {},
};

export const getVideoInImgSources = (url) => {
  const { assetHostTCP } = getAPIConfig();
  const urlVal = isAbsoluteUrl(url) ? cleanUpUrl(url) : url;
  const assetHostVal = isAbsoluteUrl(url) ? getAssetHostVal(url, assetHostTCP) : assetHostTCP;
  const assetHost = assetHostVal.replace('/image/', '/video/');
  return {
    webpUrl: `${assetHost}/e_loop,fl_animated.awebp,q_auto:best/${urlVal}.webp`,
    mp4Url: `${assetHost}/f_mp4,vc_auto,q_auto/${urlVal}.mp4`,
  };
};

export const RenderVideoInImg = (props) => {
  const { videoData, preloadImage, className } = props;
  const videoUrls = getVideoUrls(videoData);
  const [source, setSource] = useState({});
  const posterUrls = getPosterUrls(videoData);
  const [poster, setPoster] = useState('');
  const mobilePoster = findPosterImage(posterUrls, videoUrls, 'mobile');
  const desktopPoster = findPosterImage(posterUrls, videoUrls, 'desktop');
  const videoPosterPreload = getVideoPosterPreload(preloadImage, desktopPoster, mobilePoster);
  const aspectRatioObj = getAspectRatio(null, videoData, null);
  useEffect(() => {
    const { isDesktop, isTablet, isMobile } = getViewportInfo();
    if (isDesktop && videoUrls.desktop) {
      setSource(getVideoInImgSources(videoUrls.desktop));
      setPoster(findPosterImage(posterUrls, videoUrls, 'desktop'));
    }
    if (isTablet && videoUrls.tablet) {
      setSource(getVideoInImgSources(videoUrls.tablet));
      setPoster(findPosterImage(posterUrls, videoUrls, 'tablet'));
    }
    if (isMobile && videoUrls.mobile) {
      setSource(getVideoInImgSources(videoUrls.mobile));
      setPoster(findPosterImage(posterUrls, videoUrls, 'mobile'));
    }
  }, [videoData]);

  return (
    <>
      {videoPosterPreload}
      <picture>
        <source srcSet={source?.webpUrl} type="image/webp" />
        <source srcSet={source?.mp4Url} />
        <StyledImage
          src={poster}
          alt={videoData.title}
          overrideStyle={getVideoStyle(videoData)}
          aspectRatioObj={aspectRatioObj}
          className={className}
        />
      </picture>
    </>
  );
};

RenderVideoInImg.propTypes = {
  videoData: PropTypes.shape({}),
  preloadImage: PropTypes.bool,
};

RenderVideoInImg.defaultProps = {
  videoData: {},
  preloadImage: false,
};

export const getProductAssetUrl = ({ url = '' }, primaryBrand, itemBrand) => {
  const path = removeExtension(url);
  const apiConfig = getAPIConfig();
  let { brandId } = apiConfig;
  if (itemBrand) {
    brandId = itemBrand;
  }
  if (primaryBrand) {
    brandId = primaryBrand;
  }
  const assetPrefix = apiConfig[`productAssetPath${brandId.toUpperCase()}`];
  return `${assetPrefix}/${path}`;
};

export const getProductVideoUrls = (url, crop) => {
  const [mobile, tablet, desktop] = crop;
  const { assetHostTCP } = getAPIConfig();
  const assetHost = assetHostTCP.replace('/image/', '/video/');
  return {
    mobile: `${assetHost}/${mobile}/${url}`,
    tablet: `${assetHost}/${tablet}/${url}`,
    desktop: `${assetHost}/${desktop}/${url}`,
  };
};

export const ProductVideo = (props) => {
  const {
    url,
    crops,
    altText,
    isOptimizedVideo,
    disableVideoRender,
    primaryBrand,
    itemBrand,
    forceWebpForVideo,
    preloadImage,
  } = props;
  const path = getProductAssetUrl(url, primaryBrand, itemBrand);
  const urls = getProductVideoUrls(path, crops);
  const [source, setSources] = useState({
    video: forceWebpForVideo ? urls.mobile.concat('.webp') : urls.mobile.concat('.mp4'),
    poster: urls.mobile.concat('.jpg'),
  });
  useEffect(() => {
    const { isTablet, isDesktop } = getViewportInfo();
    const extn = (() => {
      if (disableVideoRender) return '.jpg';
      if (!isOptimizedVideo || !isSafariBrowser() || forceWebpForVideo) return '.webp';
      if (isIEBrowser()) return '.gif';
      return '.mp4';
    })();
    let newUrl = urls.mobile;
    if (isTablet) {
      newUrl = urls.tablet;
    } else if (isDesktop) {
      newUrl = urls.desktop;
    }
    setSources({ video: newUrl.concat(extn), poster: newUrl.concat('.jpg') });
  }, [url]);

  if (isOptimizedVideo) {
    return <img src={source.video} alt={altText} />;
  }

  if (disableVideoRender) {
    return <img src={source.poster} alt={altText} />;
  }

  const videoData = {
    url: source.video,
    url_t: source.video,
    url_m: source.video,
  };

  return (
    <RenderVideo videoData={videoData} isModule={false} preloadImage={preloadImage} isProductData />
  );
};

ProductVideo.propTypes = {
  url: PropTypes.shape({}),
  crops: PropTypes.arrayOf(PropTypes.string),
  altText: PropTypes.string,
  isOptimizedVideo: PropTypes.bool,
  forceWebpForVideo: PropTypes.bool,
  preloadImage: PropTypes.bool,
};

ProductVideo.defaultProps = {
  url: {},
  crops: [],
  altText: '',
  isOptimizedVideo: false,
  forceWebpForVideo: false,
  preloadImage: false,
};
// eslint-disable-next-line complexity
export const DamMedia = (props) => {
  const {
    imgData,
    imgConfigs,
    alt,
    videoData: videoInfo,
    isProductImage,
    isOptimizedVideo,
    disableVideoRender,
    dynamicBadgeOverlayQty,
    badgeConfig,
    tcpStyleType,
    renderPictureTag,
    videoDataMobile,
    className,
    itemBrand,
    isHomePage,
    fromFavorites,
    imageURL,
    isSecureImageFlagEnabled,
    primaryBrand,
    tabIndex,
    onLoad,
    disableVideosForAppleDevice,
    preloadImage,
    renderDiscoveryButton,
    lazyLoadImage,
    isDamImageV2Enabled,
    isDamImageV2EnabledPDP,
    forceWebpForVideo,
    isDamImageV2EnabledPLP,
    isPlpPopUpMedia,
    aspectRatioObj,
    isEnableMute,
    isPinchZoomImage,
    selectedPDPImage,
    isPDP,
    fromQv,
    imageConfigWithoutDPR,
    fromBagPage,
    isFromHeroProductTile,
    isAddedToBagModal,
    isTransparentImageSizeReduceEnabled,
    isPngImage,
  } = props;

  const [videoData, setVideoData] = useState(videoInfo);
  const [mobileHasVideo, setMobileHasVideo] = useState(false);

  const { extraConfig } = React.useContext(LabelsContext);
  const { isHpHLSEnabled } = extraConfig || {};

  useEffect(() => {
    if (videoDataMobile && videoDataMobile.url) {
      const { isMobile } = getViewportInfo();
      if (isMobile) {
        setVideoData(videoDataMobile);
        setMobileHasVideo(true);
      }
    }
  }, []);
  if (!isTier1Device() && videoData && videoData.url) {
    return (
      <RenderVideoImage
        videoData={videoData}
        preloadImage={preloadImage}
        aspectRatioObj={aspectRatioObj}
        className={className}
      />
    );
  }

  if (isPlpPopUpMedia && videoData && videoData.url) {
    return (
      <RenderVideoImage
        videoData={videoData}
        preloadImage={preloadImage}
        aspectRatioObj={aspectRatioObj}
      />
    );
  }

  if (
    isAppleDevice() &&
    disableVideosForAppleDevice &&
    videoData &&
    videoData.url &&
    !parseBoolean(videoData.hls)
  ) {
    return (
      <RenderVideoInImg videoData={videoData} preloadImage={preloadImage} className={className} />
    );
  }

  if ((imgData && imgData.url && !mobileHasVideo) || fromFavorites || imageURL) {
    if (isProductImage && isVideo(imgData.url)) {
      // there is a bunch of business logic surrounding product videos so keep it separate
      return (
        <ProductVideo
          url={imgData}
          crops={imgConfigs}
          altText={imgData?.alt}
          isOptimizedVideo={isOptimizedVideo}
          disableVideoRender={disableVideoRender}
          primaryBrand={primaryBrand}
          itemBrand={itemBrand}
          forceWebpForVideo={forceWebpForVideo}
          preloadImage={preloadImage}
        />
      );
    }
    return (
      <RenderImage
        imgData={imgData}
        imgConfigs={imgConfigs}
        alt={alt || imgData?.alt || imgData?.title}
        isProductImage={isProductImage}
        dynamicBadgeOverlayQty={dynamicBadgeOverlayQty}
        badgeConfig={badgeConfig}
        tcpStyleType={tcpStyleType}
        renderPictureTag={renderPictureTag}
        className={className}
        itemBrand={itemBrand}
        isHomePage={isHomePage}
        fromFavorites={fromFavorites}
        imageURL={imageURL}
        isSecureImageFlagEnabled={isSecureImageFlagEnabled}
        primaryBrand={primaryBrand}
        tabIndex={tabIndex}
        onLoad={onLoad}
        preloadImage={preloadImage}
        renderDiscoveryButton={renderDiscoveryButton}
        lazyLoadImage={lazyLoadImage}
        isDamImageV2Enabled={isDamImageV2Enabled}
        isDamImageV2EnabledPDP={isDamImageV2EnabledPDP}
        isDamImageV2EnabledPLP={isDamImageV2EnabledPLP}
        isPinchZoomImage={isPinchZoomImage}
        selectedPDPImage={selectedPDPImage}
        isPDP={isPDP}
        fromQv={fromQv}
        imageConfigWithoutDPR={imageConfigWithoutDPR}
        fromBagPage={fromBagPage}
        isFromHeroProductTile={isFromHeroProductTile}
        isAddedToBagModal={isAddedToBagModal}
        isTransparentImageSizeReduceEnabled={isTransparentImageSizeReduceEnabled}
        isPngImage={isPngImage}
      />
    );
  }
  if (videoData && parseBoolean(videoData.hls) && isHpHLSEnabled) {
    const { isModule, assignKeyValue } = props;
    return (
      <CloudinaryVideoPlayer
        videoData={videoData}
        isModule={isModule}
        preloadImage={preloadImage}
        assignKeyValue={assignKeyValue}
        isEnableMute={isEnableMute}
      />
    );
  }

  if (videoData && videoData.url) {
    const { isModule, onScrollIdentifier } = props;
    return (
      <RenderVideo
        onScrollIdentifier={onScrollIdentifier}
        videoData={videoData}
        isModule={isModule}
        preloadImage={preloadImage}
        isEnableMute={isEnableMute}
        className={className}
      />
    );
  }
  return <></>;
};

DamMedia.propTypes = {
  onLoad: PropTypes.func,
  imgData: PropTypes.shape({}),
  videoData: PropTypes.shape({}),
  imgConfigs: PropTypes.arrayOf(PropTypes.string),
  alt: PropTypes.string,
  link: PropTypes.shape({}),
  isProductImage: PropTypes.bool,
  isOptimizedVideo: PropTypes.bool,
  noSourcePictureTag: PropTypes.bool,
  className: PropTypes.string,
  disableVideosForAppleDevice: PropTypes.bool,
  preloadImage: PropTypes.bool,
  assignKeyValue: PropTypes.string.isRequired,
  selectedPDPImage: PropTypes.string,
  isPDP: PropTypes.bool,
  fromQv: PropTypes.bool,
  imageConfigWithoutDPR: PropTypes.string,
  fromBagPage: PropTypes.bool,
  useRouterNavigation: PropTypes.bool,
  isFromHeroProductTile: PropTypes.bool,
  isTransparentImageSizeReduceEnabled: PropTypes.bool,
  isPngImage: PropTypes.bool,
};

DamMedia.defaultProps = {
  onLoad: () => null,
  imgData: {},
  videoData: {},
  imgConfigs: [defaultImageCrop, defaultImageCrop, defaultImageCrop],
  alt: '',
  link: {},
  isProductImage: false,
  isOptimizedVideo: false,
  noSourcePictureTag: false,
  className: '',
  disableVideosForAppleDevice: true,
  preloadImage: false,
  selectedPDPImage: '',
  isPDP: false,
  fromQv: false,
  imageConfigWithoutDPR: '',
  fromBagPage: false,
  useRouterNavigation: false,
  isFromHeroProductTile: false,
  isTransparentImageSizeReduceEnabled: false,
  isPngImage: false,
};

const checkIfPlayPauseButton = (element) => {
  const eleType = element.nodeName;
  const { className } = element;
  const hasVjsClassName =
    className.includes('video-play-btn') || className.includes('vjs-icon-placeholder');
  const parentType = element.parentNode.nodeName;
  return (eleType === 'IMG' && parentType === 'BUTTON') || hasVjsClassName;
};

const DamImage = (props) => {
  const {
    link,
    dataLocator,
    showPlaceHolder,
    onScrollIdentifier,
    noLink,
    isPlpPopUpMedia,
    useRouterNavigation,
    isModule,
    moduleAnalyticsTag,
    isModuleQVEnabled,
    moduleQVProductId,
  } = props;
  const [openPlpPopUpMedia, setOpenPlpPopUpMedia] = useState(false);
  const [quickViewMedia, setQuickViewMedia] = useState(false);
  if (!noLink && link) {
    const { url: ctaUrl, target, title, actualUrl, className: ctaClassName, text } = link || {};
    let to = actualUrl;
    if (!actualUrl && ctaUrl) {
      to = configureInternalNavigationFromCMSUrl(ctaUrl);
    }
    const handleClick = (e) => {
      if (target === '_modal' && isPlpPopUpMedia) {
        e.preventDefault();
        setOpenPlpPopUpMedia(true);
      }
      if (target === '_modal' && isModuleQVEnabled && checkIfPlayPauseButton(e.target)) {
        e.preventDefault();
      }
      if (target === '_modal' && isModuleQVEnabled && !checkIfPlayPauseButton(e.target)) {
        e.preventDefault();
        setQuickViewMedia(true);
      }
      if (e.currentTarget !== e.target && checkIfPlayPauseButton(e.target)) {
        e.preventDefault(); // necessary to prevent default for video otherwise play pause button clicking navigates to anchor url
      }
    };
    const onClickProps =
      useRouterNavigation && target !== '_modal' && (!isPlpPopUpMedia || !isModuleQVEnabled)
        ? {
            useRouterNavigation,
            handleLinkClick: handleClick,
            isModule,
          }
        : {
            onClick: handleClick,
            noLink,
            useRouterNavigation: false,
          };
    const updatedToUrl = updateUrlWithIcid(to, moduleAnalyticsTag);
    const updatedCtaUrl = updateUrlWithIcid(ctaUrl, moduleAnalyticsTag);
    return (
      <>
        <Anchor
          className={`${ctaClassName} ${showPlaceHolder ? 'full-width' : ''}`}
          to={updatedToUrl}
          asPath={updatedCtaUrl}
          target={target}
          title={title || text}
          dataLocator={`${dataLocator}_image-link`}
          {...onClickProps}
        >
          <DamMedia onScrollIdentifier={onScrollIdentifier} {...props} />
        </Anchor>
        {openPlpPopUpMedia && (
          <PlpPopUpMedia
            openPlpPopUpMedia={openPlpPopUpMedia}
            setOpenPlpPopUpMedia={setOpenPlpPopUpMedia}
            {...props}
          />
        )}
        {quickViewMedia && (
          <ModuleQuickViewWrapper
            quickViewMedia={quickViewMedia}
            setQuickViewMedia={setQuickViewMedia}
            moduleQVProductId={moduleQVProductId}
            {...props}
          />
        )}
      </>
    );
  }
  return <DamMedia onScrollIdentifier={onScrollIdentifier} {...props} />;
};

export default DamImage;
export { DamImage as DamImageVanilla };
/* eslint-enable */
